import { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';

interface Case {
  id: number;
  client_name: string;
  file_reference: string;
  case_type: string;
  team_name: string;
  priority: string;
  next_deadline_date: string;
  file_status: string;
}

function daysUntil(dateStr: string): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function priorityBadgeClass(p: string | null): string {
  if (!p) return 'badge badge-gray';
  const lp = p.toLowerCase();
  if (lp === 'high') return 'badge badge-red';
  if (lp === 'medium') return 'badge badge-yellow';
  if (lp === 'low') return 'badge badge-green';
  return 'badge badge-gray';
}

function urgencyClass(days: number): string {
  if (days < 0) return 'badge badge-red';
  if (days <= 7) return 'badge badge-red';
  if (days <= 14) return 'badge badge-yellow';
  return 'badge badge-green';
}

export default function DeadlinesPage() {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'overdue' | 'this_week' | 'this_month'>('all');

  useEffect(() => {
    apiFetch('/api/deadlines')
      .then(r => r.json())
      .then((data: Case[]) => { setCases(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = cases.filter(c => {
    const days = daysUntil(c.next_deadline_date);
    if (filter === 'overdue') return days < 0;
    if (filter === 'this_week') return days >= 0 && days <= 7;
    if (filter === 'this_month') return days >= 0 && days <= 30;
    return true;
  });

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Deadlines</h1>
          <p className="page-subtitle">Track upcoming case deadlines</p>
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 20 }}>
        {[
          { key: 'all', label: 'All' },
          { key: 'overdue', label: 'Overdue' },
          { key: 'this_week', label: 'This Week' },
          { key: 'this_month', label: 'This Month' },
        ].map(f => (
          <button key={f.key} className={`tab${filter === f.key ? ' active' : ''}`} onClick={() => setFilter(f.key as typeof filter)}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="table-container">
          {filtered.length === 0 ? (
            <div className="empty-state"><p>No deadlines found for this filter.</p></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>File Ref</th>
                  <th>Case Type</th>
                  <th>Team</th>
                  <th>Priority</th>
                  <th>Deadline</th>
                  <th>Days Until</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const days = daysUntil(c.next_deadline_date);
                  return (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.client_name}</td>
                      <td>{c.file_reference}</td>
                      <td>{c.case_type || '-'}</td>
                      <td>{c.team_name || '-'}</td>
                      <td><span className={priorityBadgeClass(c.priority)}>{c.priority}</span></td>
                      <td>{new Date(c.next_deadline_date).toLocaleDateString('en-GB')}</td>
                      <td>
                        <span className={urgencyClass(days)}>
                          {days < 0 ? `${Math.abs(days)} days overdue` : days === 0 ? 'Today' : `${days} days`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
